import type { ReactNode } from 'react';
import { Card, CardHeader, CardTitle } from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import { PeriodChart } from '../period/PeriodChart';
import { periodLabel, tileLabel, type Period, type PeriodChoice } from '../period/period';
import { HowMeasured } from './CostEstimate';
import { PartnerInvoicesCard } from './PartnerInvoicesCard';
import { RevenueFigures } from './RevenueFigures';
import { RevenueTable, type RenderCompany, type RevenueTableProps } from './RevenueTable';
import { margin } from './revenue-math';
import type { RevenuePoint } from './revenue-types';
import '../admin.css';
import './revenue.css';

export interface RevenueBoardProps extends Omit<RevenueTableProps, 'renderLink'> {
  /** The period control's choice: it words every label. */
  choice: PeriodChoice;
  /** A bucket of the chart was chosen: show those days only. */
  onPick?: ((bucket: Period) => void) | undefined;
  renderLink?: RenderCompany | undefined;
  /** A partner row's link to its partner account page. */
  renderPartner?: ((zohoAccountId: string, name: string) => ReactNode) | undefined;
  /** The month's QuickBooks import of every account's issued statements (yyyy-MM). */
  statementsCsv?: ((month: string) => string) | undefined;
}

/** A bucket's margin for the tooltip: dollars and percent, or "no revenue". */
function marginLine(f: Formatter, point: RevenuePoint | undefined): string[] {
  if (!point) return [];
  const m = margin(point);
  return [
    m === null
      ? 'Margin: no revenue'
      : `Margin: ${f.money(point.net - point.cost)} (${f.percent(m, 0)})`,
  ];
}

/** Card money as bars (left axis) and processing cost as a line (its own right axis); a bucket narrows the period. */
function MoneyChart({ revenue, choice, onPick }: RevenueBoardProps): ReactNode {
  const f = useFormat();
  const { series, period } = revenue;
  const title = tileLabel('Collected and cost', choice, period);
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <PeriodChart
        label={`${title}, by ${revenue.bucket}`}
        buckets={series}
        series={[
          {
            key: 'net',
            label: 'Collected by card',
            values: series.map((p) => p.net),
            kind: 'bars',
            format: f.money,
          },
          {
            key: 'cost',
            label: 'Processing cost',
            values: series.map((p) => p.cost),
            kind: 'line',
            // Its own function, so its own axis: cost is a hundredth of the money, and on the money's
            // axis it would lie flat on the baseline and say nothing.
            format: (n) => f.money(n),
            tone: 'muted',
          },
        ]}
        tipLines={(i) => marginLine(f, series[i])}
        onPick={onPick}
        height={240}
        empty={`No card money and no processing cost, ${periodLabel(period)}.`}
      />
    </Card>
  );
}

/**
 * Revenue: what came in by card, what the work cost and the margin, large; the period as a chart; then
 * the companies a page at a time, sorted by the ledger. How it is measured sits behind a disclosure.
 * Every total is the ledger's own (`totals`), never a sum of the rows on the page.
 */
export function RevenueBoard(props: RevenueBoardProps): ReactNode {
  const { revenue, choice } = props;
  return (
    <div className="ad-revenue">
      <Card>
        <RevenueFigures revenue={revenue} choice={choice} />
      </Card>
      <MoneyChart {...props} />
      <PartnerInvoicesCard
        revenue={revenue}
        choice={choice}
        renderPartner={props.renderPartner}
        statementsCsv={props.statementsCsv}
      />
      <Card>
        <CardHeader>
          <CardTitle>{tileLabel('By company', choice, revenue.period)}</CardTitle>
        </CardHeader>
        <RevenueTable {...props} renderLink={props.renderLink ?? ((_tenant, name) => name)} />
        <HowMeasured revenue={revenue} />
      </Card>
    </div>
  );
}
